import { useState } from "react";
import { ClipboardList, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

const show = (v) => (v === true ? "Yes" : v === false ? "No" : v);

const Answer = ({ field, value }) =>
  field.type === "signature" ? (
    <img src={value} alt="Signature" className="h-12 w-auto" />
  ) : (
    <>{show(value)}</>
  );

export const CreditApplicationView = ({ customerId, customerName }) => {
  const [open, setOpen] = useState(false);
  const [app, setApp] = useState(null);

  const load = async () => {
    setOpen(true);
    try {
      const { data } = await api.get(`/company/customers/${customerId}/credit-application`);
      setApp(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const filled = (section) =>
    section.fields.filter((f) => String(app?.data?.[f.key] ?? "") !== "" && app.data[f.key] !== false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          data-testid={`view-application-${customerId}`}
          className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
        >
          <ClipboardList className="mr-1.5 h-3.5 w-3.5" /> Application
        </Button>
      </DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        className="max-h-[85vh] max-w-3xl overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight">
            Credit application — {customerName}
          </DialogTitle>
        </DialogHeader>

        {!app ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : !Object.keys(app.data || {}).length ? (
          <p className="text-sm text-slate-500" data-testid="application-empty">
            {customerName} has not started their credit application yet.
          </p>
        ) : (
          <div id={`application-print-${customerId}`} className="space-y-6">
            <p className="text-sm text-slate-500">
              {app.submitted_at
                ? `Submitted ${app.submitted_at.slice(0, 10)}`
                : `Draft — last saved ${(app.updated_at || "").slice(0, 10)}`}
            </p>
            {app.sections.map((s) =>
              filled(s).length ? (
                <div key={s.title}>
                  <h3 className="font-display text-base font-bold text-slate-900">{s.title}</h3>
                  <dl className="mt-3 divide-y divide-slate-100 rounded-md border border-slate-200">
                    {filled(s).map((f) => (
                      <div key={f.key} className="grid grid-cols-2 gap-4 px-4 py-2.5 text-sm">
                        <dt className="text-slate-500">{f.label}</dt>
                        <dd className="font-medium text-slate-900">
                          <Answer field={f} value={app.data[f.key]} />
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null
            )}
          </div>
        )}

        <div className="mt-2 flex justify-end">
          <Button
            variant="outline"
            onClick={() => window.print()}
            data-testid={`print-application-${customerId}`}
            className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
          >
            <Printer className="mr-2 h-4 w-4" /> Print
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
